/**
 * NOS-248 (M22.3): Shared cache policy for the public website.
 *
 * Pure module — no app/DOM/SDK imports — so the Astro public app (NOS-247,
 * `apps/public/`) can consume it for response headers, the SPA can consume it
 * for purge planning, and tests can pin the contract.
 *
 * Contract (spec §5 D2/D3):
 * - Pages serve `Cache-Control: public, s-maxage=N, stale-while-revalidate=Z`
 *   only — standard headers, no host-proprietary primitives.
 * - Donation/registration paths and preview URLs are NEVER cached.
 * - Every cacheable response carries cache tags (`Vercel-Cache-Tag` today):
 *   an org-wide tag plus a per-path tag, so invalidation can target one URL
 *   or the whole site with a single tag purge. Tags are host-agnostic, which
 *   makes one purge cover both `{slug}.getnos.ai` and the custom domain.
 *
 * The tag format is mirrored in `supabase/functions/_shared/cacheTags.ts`
 * (Deno can't share this file). Both sides pin the exact strings in tests —
 * if you change the format here, change it there and both tests.
 */

export type PublicRouteType =
  | "home"
  | "page"
  | "blog-index"
  | "blog-post"
  | "events-index"
  | "event-detail"
  | "media-index"
  | "media-detail"
  | "campaign-index"
  | "campaign-detail"
  | "sitemap"
  | "robots"
  | "never-cache"

interface Ttl {
  sMaxage: number
  swr: number
}

/** Per-route-type TTLs in seconds: fresh window + stale-while-revalidate window. */
export const ROUTE_TTLS: Record<Exclude<PublicRouteType, "never-cache">, Ttl> = {
  home: { sMaxage: 300, swr: 86400 },
  page: { sMaxage: 300, swr: 86400 },
  "blog-index": { sMaxage: 300, swr: 86400 },
  "blog-post": { sMaxage: 600, swr: 86400 },
  "events-index": { sMaxage: 300, swr: 3600 },
  // Event/campaign details show registration capacity and live donation
  // progress — keep the fresh window tight.
  "event-detail": { sMaxage: 60, swr: 600 },
  "media-index": { sMaxage: 300, swr: 86400 },
  "media-detail": { sMaxage: 300, swr: 86400 },
  "campaign-index": { sMaxage: 300, swr: 3600 },
  "campaign-detail": { sMaxage: 60, swr: 600 },
  sitemap: { sMaxage: 3600, swr: 86400 },
  robots: { sMaxage: 3600, swr: 86400 },
}

export const NEVER_CACHE_CONTROL = "private, no-cache, no-store, must-revalidate"

/**
 * Path prefixes that must never be cached: donation and registration flows
 * (payment/PII responses) and personal fundraiser share pages (per-token
 * content). Matched against the first path segment or as a segment anywhere
 * (fundraise is nested under /campaign/:slug/fundraise/:token).
 */
const NEVER_CACHE_SEGMENTS = new Set([
  "donate",
  "donation",
  "donations",
  "give",
  "checkout",
  "register",
  "registration",
  "rsvp",
  "fundraise",
  "preview",
  "thank-you",
])

/** Query params that mark a builder/dashboard preview of the public site. */
const PREVIEW_PARAMS = ["preview", "draft"]

/** Normalize a public path: leading slash, no query/hash, no trailing slash, lowercase. */
export function normalizePublicPath(path: string): string {
  let p = path.trim()
  const cut = Math.min(
    ...[p.indexOf("?"), p.indexOf("#")].filter((i) => i >= 0)
  )
  if (Number.isFinite(cut)) p = p.slice(0, cut)
  p = "/" + p.replace(/^\/+/, "")
  p = p.replace(/\/{2,}/g, "/")
  if (p.length > 1) p = p.replace(/\/+$/, "")
  return p.toLowerCase()
}

export function isPreviewRequest(search: string | undefined): boolean {
  if (!search) return false
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search)
  return PREVIEW_PARAMS.some((key) => {
    const v = params.get(key)
    return v !== null && v !== "false" && v !== "0"
  })
}

export function isNeverCachePath(pathname: string, search?: string): boolean {
  if (isPreviewRequest(search)) return true
  const segments = normalizePublicPath(pathname).split("/").filter(Boolean)
  return segments.some((s) => NEVER_CACHE_SEGMENTS.has(s))
}

export function classifyPublicRoute(pathname: string, search?: string): PublicRouteType {
  if (isNeverCachePath(pathname, search)) return "never-cache"
  const path = normalizePublicPath(pathname)
  const segments = path.split("/").filter(Boolean)
  if (segments.length === 0) return "home"
  if (path === "/sitemap.xml") return "sitemap"
  if (path === "/robots.txt") return "robots"
  const [head] = segments
  if (head === "blog") return segments.length === 1 ? "blog-index" : "blog-post"
  if (head === "events") return segments.length === 1 ? "events-index" : "event-detail"
  if (head === "media") return segments.length === 1 ? "media-index" : "media-detail"
  if (head === "campaign") {
    return segments.length === 1 ? "campaign-index" : "campaign-detail"
  }
  return "page"
}

export function getCacheControl(routeType: PublicRouteType): string {
  if (routeType === "never-cache") return NEVER_CACHE_CONTROL
  const { sMaxage, swr } = ROUTE_TTLS[routeType]
  return `public, s-maxage=${sMaxage}, stale-while-revalidate=${swr}`
}

// --- Cache tags (format mirrored in supabase/functions/_shared/cacheTags.ts) ---

const TAG_PATH_MAX = 200

/** Org-wide tag: attached to every cached page; purging it clears the whole site. */
export function orgCacheTag(orgId: string): string {
  return `org:${orgId}`
}

/** Per-path tag. Commas are reserved as tag delimiters; long paths are hashed. */
export function pathCacheTag(orgId: string, path: string): string {
  let p = normalizePublicPath(path).replace(/,/g, "%2C")
  if (p.length > TAG_PATH_MAX) {
    let hash = 5381
    for (let i = 0; i < p.length; i++) hash = ((hash * 33) ^ p.charCodeAt(i)) >>> 0
    p = p.slice(0, TAG_PATH_MAX) + "~" + hash.toString(16)
  }
  return `org:${orgId}:path:${p}`
}

/**
 * Response headers for a public-site request. Never-cache routes get only the
 * no-store Cache-Control; cacheable routes also get their purge tags.
 */
export function getPublicCacheHeaders(
  orgId: string,
  pathname: string,
  search?: string
): Record<string, string> {
  const routeType = classifyPublicRoute(pathname, search)
  const headers: Record<string, string> = {
    "Cache-Control": getCacheControl(routeType),
  }
  if (routeType !== "never-cache") {
    headers["Vercel-Cache-Tag"] = `${orgCacheTag(orgId)},${pathCacheTag(orgId, pathname)}`
  }
  return headers
}

/** Public path for a builder page slug (`home` renders at the site root). */
export function pagePathForSlug(slug: string): string {
  const s = slug.replace(/^\/+/, "")
  return s === "home" || s === "" ? "/" : normalizePublicPath(s)
}
